import { ChevronDown } from 'lucide-react'
import React, { useState } from 'react'

import { cn } from '@/lib/utils'

type SectionProps = {
  children: React.ReactNode
  title?: React.ReactNode
  extra?: React.ReactNode
  defaultActiveKey?: string[]
  /** Start expanded (uncontrolled). Overridden by `defaultActiveKey`. */
  defaultOpen?: boolean
  /** Controlled open state. When provided, `onOpenChange` should update it. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function Section({
  children,
  title,
  extra,
  defaultActiveKey,
  defaultOpen,
  open: controlledOpen,
  onOpenChange
}: SectionProps) {
  const [internalOpen, setInternalOpen] = useState(
    (!!defaultActiveKey && defaultActiveKey.length > 0) || !!defaultOpen
  )

  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen

  const toggle = () => {
    const next = !open
    if (!isControlled) setInternalOpen(next)
    onOpenChange?.(next)
  }

  return (
    <div className="box-border w-full overflow-hidden rounded-2xl border border-ink/[0.06] bg-white shadow-[0_10px_30px_rgba(4,33,37,0.06)] [&_*]:box-border">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggle()
          }
        }}
        className="group flex w-full cursor-pointer select-none items-center gap-3 px-5 py-4 transition-colors hover:bg-ink/[0.015]"
      >
        <div className="min-w-0 flex-1">{title}</div>
        {extra && <div onClick={(e) => e.stopPropagation()}>{extra}</div>}
        <span
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors',
            open
              ? 'bg-primary text-white'
              : 'bg-primary/10 text-primary group-hover:bg-primary/20'
          )}
        >
          <ChevronDown
            className={cn(
              'h-5 w-5 transition-transform duration-300',
              open && 'rotate-180'
            )}
          />
        </span>
      </div>

      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-out',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="border-t border-ink/[0.06] px-5 py-5">{children}</div>
        </div>
      </div>
    </div>
  )
}
